/**
 * Super Admin Dashboard
 * 
 * Main dashboard for system-wide administration.
 * Shows statistics, organizations, and recent activity.
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSuperAdmin } from '../../context/SuperAdminContext';
import Grid from '@mui/material/GridLegacy';
import {
  Container,
  Paper,
  Typography,
  Box,
  Card,
  CardContent,
  CardActionArea,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemAvatar,
  Divider,
  Button,
  Chip,
  CircularProgress,
  Alert,
  AppBar,
  Toolbar,
  IconButton,
  Menu,
  MenuItem,
  Avatar,
  LinearProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tab,
  Collapse,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Pagination,
} from '@mui/material';
import {
  Business,
  People,
  Description,
  Receipt,
  Add,
  Settings,
  Logout,
  AdminPanelSettings,
  Timeline,
  KeyboardArrowDown,
  KeyboardArrowUp,
  ContentCopy,
  ToggleOn,
  PersonAdd,
  Edit,
  TrendingUp,
  Shield,
  Speed,
  Refresh,
  ArrowForward,
  CheckCircle,
  Schedule,
  AttachMoney,
  PendingActions,
  Done,
  Close,
  ExpandMore,
  ExpandLess,
  Person,
  DirectionsCar,
  Timer as TimerIcon,
} from '@mui/icons-material';

// ─── Super Admin Session Timer ──────────────────────────────────────
// Set SHOW_SESSION_TIMER to true to display the countdown in the AppBar
const SHOW_SESSION_TIMER = true;

// Hidden — session timer is removed from super admin screen
function SuperAdminSessionTimer() {
  return null;
}

const SuperAdminDashboard = () => {
  const navigate = useNavigate();
  const { superAdmin, api, logout, isAuthenticated, loading: authLoading } = useSuperAdmin();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [anchorEl, setAnchorEl] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [expandedOrg, setExpandedOrg] = useState(null);

  // "View All" permits dialog for a single organization (Permits tab)
  const [permitsDialogOrg, setPermitsDialogOrg] = useState(null);
  const [orgPermits, setOrgPermits] = useState([]);
  const [orgPermitsLoading, setOrgPermitsLoading] = useState(false);
  const [orgPermitsError, setOrgPermitsError] = useState('');
  const [orgPermitsPage, setOrgPermitsPage] = useState(1);
  const [orgPermitsTotalPages, setOrgPermitsTotalPages] = useState(1);

  // "View All" users dialog for a single organization (Users tab)
  const [usersDialogOrg, setUsersDialogOrg] = useState(null);
  const [orgUsersList, setOrgUsersList] = useState([]);
  const [orgUsersLoading, setOrgUsersLoading] = useState(false);
  const [orgUsersError, setOrgUsersError] = useState('');
  const [orgUsersPage, setOrgUsersPage] = useState(1);
  const [orgUsersTotalPages, setOrgUsersTotalPages] = useState(1);

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated) {
      navigate('/superadmin/login');
      return;
    }
    fetchDashboardData();

    // Auto-refresh dashboard data every 30 seconds so that
    // permit creations, rejections, approvals etc. performed
    // on the organization side are reflected in real-time.
    const REFRESH_INTERVAL_MS = 30000;
    const intervalId = setInterval(fetchDashboardData, REFRESH_INTERVAL_MS);

    return () => clearInterval(intervalId);
  }, [isAuthenticated, authLoading, navigate]);

  const fetchDashboardData = async () => {
    try {
      const response = await api.get('/api/superadmin/dashboard/');
      setStats(response.data);
    } catch (err) {
      console.error('Dashboard error:', err);
      setError(err.response?.data?.error || err.message || 'Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const toggleOrgExpand = (orgId) => {
    setExpandedOrg(expandedOrg === orgId ? null : orgId);
  };

  const fetchOrgPermits = async (orgId, page) => {
    setOrgPermitsLoading(true);
    setOrgPermitsError('');
    try {
      const response = await api.get(`/api/superadmin/organizations/${orgId}/permits/?page=${page}&page_size=20`);
      setOrgPermits(response.data.results || []);
      setOrgPermitsTotalPages(response.data.total_pages || 1);
      setOrgPermitsPage(response.data.page || page);
    } catch (err) {
      setOrgPermitsError(err.response?.data?.error || err.message || 'Failed to load permits');
      setOrgPermits([]);
    } finally {
      setOrgPermitsLoading(false);
    }
  };

  const openOrgPermits = (org) => {
    setPermitsDialogOrg(org);
    fetchOrgPermits(org.org_id, 1);
  };

  const closeOrgPermits = () => {
    setPermitsDialogOrg(null);
    setOrgPermits([]);
    setOrgPermitsError('');
  };

  const fetchOrgUsers = async (orgId, page) => {
    setOrgUsersLoading(true);
    setOrgUsersError('');
    try {
      const response = await api.get(`/api/superadmin/organizations/${orgId}/users/?page=${page}&page_size=20`);
      setOrgUsersList(response.data.results || []);
      setOrgUsersTotalPages(response.data.total_pages || 1);
      setOrgUsersPage(response.data.page || page);
    } catch (err) {
      setOrgUsersError(err.response?.data?.error || err.message || 'Failed to load users');
      setOrgUsersList([]);
    } finally {
      setOrgUsersLoading(false);
    }
  };

  const openOrgUsers = (org) => {
    setUsersDialogOrg(org);
    fetchOrgUsers(org.org_id, 1);
  };

  const closeOrgUsers = () => {
    setUsersDialogOrg(null);
    setOrgUsersList([]);
    setOrgUsersError('');
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-PK', {
      style: 'currency',
      currency: 'PKR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount || 0);
  };

  const handleLogout = async () => {
    await logout();
    navigate('/superadmin/login');
  };

  const getActionIcon = (action) => {
    const icons = {
      login: <AdminPanelSettings fontSize="small" />,
      logout: <Logout fontSize="small" />,
      create_org: <Add fontSize="small" />,
      update_org: <Edit fontSize="small" />,
      clone_org: <ContentCopy fontSize="small" />,
      enable_feature: <ToggleOn fontSize="small" />,
      disable_feature: <ToggleOn fontSize="small" />,
      create_admin: <PersonAdd fontSize="small" />,
    };
    return icons[action] || <Timeline fontSize="small" />;
  };

  const getActionColor = (action) => {
    const colors = {
      login: '#10b981',
      logout: '#f59e0b',
      create_org: '#3b82f6',
      update_org: '#6366f1',
      clone_org: '#8b5cf6',
      enable_feature: '#10b981',
      disable_feature: '#ef4444',
      create_admin: '#3b82f6',
    };
    return colors[action] || '#6b7280';
  };

  const formatTimeAgo = (timestamp) => {
    const now = new Date();
    const time = new Date(timestamp);
    const diff = Math.floor((now - time) / 1000);

    if (diff < 60) return 'Just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
  };

  if (loading) {
    return (
      <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        width: '100%',
        background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
      }}>
        <CircularProgress sx={{ color: '#818cf8' }} size={48} />
        <Typography sx={{ mt: 2, color: '#94a3b8' }}>Loading dashboard...</Typography>
      </Box>
    );
  }

  const quickActions = [
    { icon: <Add />, label: 'New Organization', path: '/superadmin/organizations', color: '#3b82f6', action: () => navigate('/superadmin/organizations') },
    { icon: <Business />, label: 'Organizations', path: '/superadmin/organizations', color: '#8b5cf6', action: () => navigate('/superadmin/organizations') },
    { icon: <Settings />, label: 'System Config', path: '/superadmin/system-config', color: '#6366f1', action: () => navigate('/superadmin/system-config') },
    { icon: <Timeline />, label: 'Audit Logs', path: '/superadmin/audit-logs', color: '#10b981', action: () => navigate('/superadmin/audit-logs') },
  ];

  return (
    <Box sx={{
      minHeight: '100vh',
      width: '100%',
      background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
    }}>
      {/* App Bar */}
      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          background: 'transparent',
          backdropFilter: 'blur(10px)',
          borderBottom: '1px solid rgba(255,255,255,0.1)',
        }}
      >
        <Toolbar sx={{ px: { xs: 2, sm: 4 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{
              width: 40,
              height: 40,
              borderRadius: 2,
              background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              <Shield sx={{ color: 'white', fontSize: 24 }} />
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 700, color: 'white', display: { xs: 'none', sm: 'block' } }}>
              Super Admin
            </Typography>
          </Box>

          <Box sx={{ flexGrow: 1 }} />

          <IconButton onClick={fetchDashboardData} sx={{ color: 'white', mr: 1 }}>
            <Refresh />
          </IconButton>

          <SuperAdminSessionTimer />

          <Box
            onClick={handleMenuOpen}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              cursor: 'pointer',
              px: 2,
              py: 1,
              borderRadius: 2,
              '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' },
            }}
          >
            <Avatar sx={{ width: 36, height: 36, bgcolor: '#6366f1' }}>
              {superAdmin?.first_name?.[0] || 'S'}
            </Avatar>
            <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
              <Typography variant="body2" sx={{ color: 'white', fontWeight: 600, lineHeight: 1.2 }}>
                {superAdmin?.username}
              </Typography>
              <Typography variant="caption" sx={{ color: '#94a3b8', lineHeight: 1 }}>
                {superAdmin?.access_level_display?.split(' - ')[0]}
              </Typography>
            </Box>
            <KeyboardArrowDown sx={{ color: '#94a3b8', display: { xs: 'none', sm: 'block' } }} />
          </Box>

          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
            PaperProps={{
              sx: {
                bgcolor: '#1e293b',
                border: '1px solid rgba(255,255,255,0.1)',
                mt: 1,
              }
            }}
          >
            <MenuItem onClick={handleLogout} sx={{ color: 'white', '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' } }}>
              <Logout sx={{ mr: 1, fontSize: 20 }} /> Logout
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      {/* Main Content */}
      <Box sx={{ px: { xs: 2, sm: 4 }, py: 4, maxWidth: 1400, mx: 'auto' }}>
        {error && (
          <Alert severity="error" sx={{ mb: 3, bgcolor: '#450a0a', color: '#fecaca', border: '1px solid #dc2626' }}>
            {error}
          </Alert>
        )}

        {/* Welcome Section */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" sx={{ color: 'white', fontWeight: 700, mb: 1 }}>
            Welcome back, {superAdmin?.first_name || 'Admin'}
          </Typography>
          <Typography sx={{ color: '#94a3b8' }}>
            Here's what's happening across your organizations today.
          </Typography>
        </Box>

        {/* Stats Cards */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {[
            {
              title: 'Organizations',
              value: stats?.organizations?.total || 0,
              subtitle: `${stats?.organizations?.active || 0} active`,
              icon: <Business />,
              gradient: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
              onClick: () => setActiveTab(1),
            },
            {
              title: 'Users',
              value: stats?.users?.total || 0,
              subtitle: `${stats?.users?.super_admins || 0} super admins`,
              icon: <People />,
              gradient: 'linear-gradient(135deg, #10b981 0%, #14b8a6 100%)',
              onClick: () => setActiveTab(2),
            },
            {
              title: 'Permits',
              value: stats?.data?.permits || 0,
              subtitle: 'Total issued',
              icon: <Description />,
              gradient: 'linear-gradient(135deg, #f59e0b 0%, #f97316 100%)',
              onClick: () => setActiveTab(3),
            },
            {
              title: 'Revenue',
              value: formatCurrency(stats?.chalan_revenue?.total_revenue || 0),
              subtitle: `${formatCurrency(stats?.chalan_revenue?.collected || 0)} collected`,
              icon: <AttachMoney />,
              gradient: 'linear-gradient(135deg, #ec4899 0%, #f43f5e 100%)',
              onClick: () => setActiveTab(4),
              isRevenue: true,
            },
          ].map((stat, index) => (
            <Grid item xs={6} md={3} key={index}>
              <Card
                sx={{
                  bgcolor: '#1e293b',
                  border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: 3,
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    borderColor: 'rgba(99,102,241,0.5)',
                    boxShadow: '0 8px 25px rgba(0,0,0,0.3)',
                  },
                }}
                onClick={stat.onClick}
              >
                <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2 }}>
                    <Box sx={{
                      width: { xs: 40, sm: 48 },
                      height: { xs: 40, sm: 48 },
                      borderRadius: 2,
                      background: stat.gradient,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}>
                      {React.cloneElement(stat.icon, { sx: { color: 'white', fontSize: { xs: 20, sm: 24 } } })}
                    </Box>
                    <ArrowForward sx={{ color: '#64748b', fontSize: 18 }} />
                  </Box>
                  <Typography variant="h4" sx={{ color: 'white', fontWeight: 700, mb: 0.5, fontSize: stat.isRevenue ? { xs: '1rem', sm: '1.25rem' } : { xs: '1.5rem', sm: '2rem' } }}>
                    {stat.isRevenue ? stat.value : (typeof stat.value === 'number' ? stat.value.toLocaleString() : stat.value)}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#94a3b8', fontWeight: 500 }}>
                    {stat.title}
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#64748b' }}>
                    {stat.subtitle}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Tabs for Detailed Views */}
        <Paper sx={{ bgcolor: '#1e293b', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 3, mb: 3 }}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              borderBottom: '1px solid rgba(255,255,255,0.1)',
              '& .MuiTab-root': { color: '#94a3b8', textTransform: 'none', fontWeight: 500 },
              '& .Mui-selected': { color: '#818cf8 !important' },
              '& .MuiTabs-indicator': { bgcolor: '#818cf8' },
            }}
          >
            <Tab label="Overview" icon={<Speed sx={{ fontSize: 18 }} />} iconPosition="start" />
            <Tab label="Organizations" icon={<Business sx={{ fontSize: 18 }} />} iconPosition="start" />
            <Tab label="Users" icon={<People sx={{ fontSize: 18 }} />} iconPosition="start" />
            <Tab label="Permits" icon={<Description sx={{ fontSize: 18 }} />} iconPosition="start" />
            <Tab label="Chalan Revenue" icon={<AttachMoney sx={{ fontSize: 18 }} />} iconPosition="start" />
          </Tabs>

          <Box sx={{ p: 3 }}>
            {/* Tab 0: Overview */}
            {activeTab === 0 && (
              <Grid container spacing={3}>
                {/* Quick Actions */}
                <Grid item xs={12} md={5} lg={4}>
                  <Typography variant="h6" sx={{ color: 'white', fontWeight: 600, mb: 3 }}>
                    Quick Actions
                  </Typography>
                  <Stack spacing={2}>
                    {quickActions.map((action, index) => (
                      <Box
                        key={index}
                        onClick={action.action}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 2,
                          p: 2,
                          borderRadius: 2,
                          bgcolor: 'rgba(255,255,255,0.03)',
                          border: '1px solid rgba(255,255,255,0.08)',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            bgcolor: 'rgba(255,255,255,0.08)',
                            borderColor: action.color,
                            transform: 'translateX(4px)',
                          },
                        }}
                      >
                        <Box sx={{
                          width: 40,
                          height: 40,
                          borderRadius: 2,
                          bgcolor: `${action.color}20`,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}>
                          {React.cloneElement(action.icon, { sx: { color: action.color, fontSize: 20 } })}
                        </Box>
                        <Typography sx={{ color: 'white', fontWeight: 500, flex: 1 }}>
                          {action.label}
                        </Typography>
                        <ArrowForward sx={{ color: '#64748b', fontSize: 18 }} />
                      </Box>
                    ))}
                  </Stack>
                </Grid>

                {/* Recent Activity */}
                <Grid item xs={12} md={7} lg={8}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                    <Typography variant="h6" sx={{ color: 'white', fontWeight: 600 }}>
                      Recent Activity
                    </Typography>
                    <Chip
                      label={`${stats?.recent_activity?.length || 0} events`}
                      size="small"
                      sx={{ bgcolor: 'rgba(99,102,241,0.2)', color: '#818cf8' }}
                    />
                  </Box>
                  {stats?.recent_activity?.length > 0 ? (
                    <Stack spacing={0}>
                      {stats.recent_activity.map((activity, index) => (
                        <Box
                          key={index}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 2,
                            py: 2,
                            px: 1,
                            borderRadius: 2,
                            '&:hover': { bgcolor: 'rgba(255,255,255,0.03)' },
                            borderBottom: index < stats.recent_activity.length - 1 ? '1px solid rgba(255,255,255,0.05)' : 'none',
                          }}
                        >
                          <Box sx={{
                            width: 36,
                            height: 36,
                            borderRadius: '50%',
                            bgcolor: `${getActionColor(activity.action)}20`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                          }}>
                            {React.cloneElement(getActionIcon(activity.action), { sx: { color: getActionColor(activity.action) } })}
                          </Box>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography variant="body2" sx={{ color: 'white', fontWeight: 500 }}>
                              <Box component="span" sx={{ color: '#818cf8' }}>{activity.admin}</Box>
                              {' '}
                              {activity.action.replace(/_/g, ' ')}
                              {activity.target && (
                                <Box component="span" sx={{ color: '#94a3b8' }}> → {activity.target}</Box>
                              )}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                            <Schedule sx={{ fontSize: 14, color: '#64748b' }} />
                            <Typography variant="caption" sx={{ color: '#64748b' }}>
                              {formatTimeAgo(activity.timestamp)}
                            </Typography>
                          </Box>
                        </Box>
                      ))}
                    </Stack>
                  ) : (
                    <Box sx={{ py: 6, textAlign: 'center' }}>
                      <Timeline sx={{ fontSize: 48, color: '#334155', mb: 2 }} />
                      <Typography sx={{ color: '#64748b' }}>No recent activity</Typography>
                    </Box>
                  )}
                </Grid>
              </Grid>
            )}

            {/* Tab 1: Organizations Details */}
            {activeTab === 1 && (
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                  <Typography variant="h6" sx={{ color: 'white', fontWeight: 600 }}>
                    Organizations ({stats?.organizations?.total || 0})
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={<Add />}
                    onClick={() => navigate('/superadmin/organizations')}
                    sx={{ bgcolor: '#6366f1', '&:hover': { bgcolor: '#4f46e5' } }}
                  >
                    Manage Organizations
                  </Button>
                </Box>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow sx={{ bgcolor: 'rgba(255,255,255,0.04)' }}>
                        {['Organization', 'Code', 'Type', 'Status', 'Users', 'Permits', 'Chalans', 'Revenue'].map(h => (
                          <TableCell key={h} sx={{ color: '#94a3b8', fontWeight: 600, borderBottom: '1px solid rgba(255,255,255,0.1)' }}>{h}</TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {stats?.organizations?.details?.map((org) => (
                        <TableRow key={org.id} sx={{ '&:hover': { backgroundColor: 'rgba(255,255,255,0.03) !important' } }}>
                          <TableCell sx={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                              <Avatar sx={{ bgcolor: '#6366f1', width: 36, height: 36 }}>
                                {org.name?.charAt(0)}
                              </Avatar>
                              <Box>
                                <Typography sx={{ color: 'white', fontWeight: 500 }}>{org.name}</Typography>
                                <Typography variant="caption" sx={{ color: '#64748b' }}>{org.email}</Typography>
                              </Box>
                            </Box>
                          </TableCell>
                          <TableCell sx={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                            <Chip label={org.code} size="small" sx={{ bgcolor: 'rgba(99,102,241,0.2)', color: '#818cf8' }} />
                          </TableCell>
                          <TableCell sx={{ color: '#94a3b8', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>{org.organization_type}</TableCell>
                          <TableCell sx={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                            <Chip
                              label={org.status}
                              size="small"
                              sx={{
                                bgcolor: org.status === 'active' ? 'rgba(16,185,129,0.2)' : 'rgba(239,68,68,0.2)',
                                color: org.status === 'active' ? '#10b981' : '#ef4444',
                              }}
                            />
                          </TableCell>
                          <TableCell sx={{ color: '#94a3b8', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <People sx={{ fontSize: 16, color: '#64748b' }} />
                              {org.users}
                            </Box>
                          </TableCell>
                          <TableCell sx={{ color: '#94a3b8', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Description sx={{ fontSize: 16, color: '#64748b' }} />
                              {org.permits}
                            </Box>
                          </TableCell>
                          <TableCell sx={{ color: '#94a3b8', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Receipt sx={{ fontSize: 16, color: '#64748b' }} />
                              {org.chalans}
                            </Box>
                          </TableCell>
                          <TableCell sx={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                            <Typography sx={{ color: '#10b981', fontWeight: 600 }}>
                              {formatCurrency(org.revenue)}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}

            {/* Tab 2: Users by Organization */}
            {activeTab === 2 && (
              <Box>
                <Typography variant="h6" sx={{ color: 'white', fontWeight: 600, mb: 3 }}>
                  Users by Organization ({stats?.users?.total || 0} total)
                </Typography>
                <Grid container spacing={3}>
                  {stats?.users?.by_org?.map((org) => (
                    <Grid item xs={12} md={6} lg={4} key={org.org_id}>
                      <Paper sx={{ bgcolor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 2, p: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Chip label={org.org_code} size="small" sx={{ bgcolor: 'rgba(99,102,241,0.2)', color: '#818cf8' }} />
                            <Typography sx={{ color: 'white', fontWeight: 500 }}>{org.org_name}</Typography>
                          </Box>
                          <Chip label={`${org.total_users} users`} size="small" sx={{ bgcolor: 'rgba(16,185,129,0.2)', color: '#10b981' }} />
                        </Box>
                        <Stack spacing={1}>
                          {org.users?.map((user) => (
                            <Box
                              key={user.id}
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 2,
                                p: 1.5,
                                bgcolor: 'rgba(255,255,255,0.02)',
                                borderRadius: 1,
                              }}
                            >
                              <Avatar sx={{ width: 32, height: 32, bgcolor: user.is_active ? '#10b981' : '#64748b' }}>
                                {user.username?.charAt(0).toUpperCase()}
                              </Avatar>
                              <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography variant="body2" sx={{ color: 'white', fontWeight: 500 }}>{user.username}</Typography>
                                <Typography variant="caption" sx={{ color: '#64748b', display: 'block', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                  {user.email}
                                </Typography>
                              </Box>
                              <Chip
                                label={user.role}
                                size="small"
                                sx={{
                                  bgcolor: user.role === 'admin' ? 'rgba(245,158,11,0.2)' : 'rgba(148,163,184,0.2)',
                                  color: user.role === 'admin' ? '#f59e0b' : '#94a3b8',
                                  fontSize: '0.7rem',
                                }}
                              />
                            </Box>
                          ))}
                          {org.total_users > 5 && (
                            <Typography
                              variant="caption"
                              onClick={() => openOrgUsers(org)}
                              sx={{
                                color: '#818cf8',
                                fontWeight: 600,
                                textAlign: 'center',
                                pt: 1,
                                cursor: 'pointer',
                                '&:hover': { textDecoration: 'underline' },
                              }}
                            >
                              View All ({org.total_users})
                            </Typography>
                          )}
                        </Stack>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            )}

            {/* Tab 3: Permits Breakdown */}
            {activeTab === 3 && (
              <Box>
                <Typography variant="h6" sx={{ color: 'white', fontWeight: 600, mb: 3 }}>
                  Permits by Organization ({stats?.data?.permits || 0} total)
                </Typography>
                <Grid container spacing={3}>
                  {stats?.permits_breakdown?.map((org) => (
                    <Grid item xs={12} md={6} key={org.org_id}>
                      <Paper sx={{ bgcolor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 2, p: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                            <Chip label={org.org_code} size="small" sx={{ bgcolor: 'rgba(99,102,241,0.2)', color: '#818cf8' }} />
                            <Typography sx={{ color: 'white', fontWeight: 600 }}>{org.org_name}</Typography>
                            {org.org_status && org.org_status !== 'active' && (
                              <Chip
                                label={org.org_status}
                                size="small"
                                sx={{
                                  bgcolor: org.org_status === 'suspended' ? 'rgba(239,68,68,0.2)' : 'rgba(148,163,184,0.2)',
                                  color: org.org_status === 'suspended' ? '#ef4444' : '#94a3b8',
                                  textTransform: 'capitalize',
                                }}
                              />
                            )}
                          </Box>
                          <Typography variant="h5" sx={{ color: 'white', fontWeight: 700 }}>{org.total}</Typography>
                        </Box>

                        {/* Status breakdown */}
                        <Grid container spacing={2} sx={{ mb: 2 }}>
                          {[
                            { label: 'Pending', value: org.pending, color: '#f59e0b' },
                            { label: 'Active', value: org.approved, color: '#10b981' },
                            { label: 'Rejected', value: org.rejected, color: '#ef4444' },
                            { label: 'Expired', value: org.expired, color: '#64748b' },
                            { label: 'Suspended', value: org.suspended, color: '#94a3b8' },
                          ].map((status) => (
                            <Grid item xs={2.4} key={status.label}>
                              <Box sx={{ textAlign: 'center', p: 1, bgcolor: 'rgba(255,255,255,0.02)', borderRadius: 1 }}>
                                <Typography variant="h6" sx={{ color: status.color, fontWeight: 700 }}>{status.value}</Typography>
                                <Typography variant="caption" sx={{ color: '#94a3b8' }}>{status.label}</Typography>
                              </Box>
                            </Grid>
                          ))}
                        </Grid>

                        {/* Recent permits */}
                        {org.recent?.length > 0 && (
                          <>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                              <Typography variant="caption" sx={{ color: '#64748b' }}>Recent Permits</Typography>
                              {org.total > org.recent.length && (
                                <Typography
                                  variant="caption"
                                  onClick={() => openOrgPermits(org)}
                                  sx={{
                                    color: '#818cf8',
                                    fontWeight: 600,
                                    cursor: 'pointer',
                                    '&:hover': { textDecoration: 'underline' },
                                  }}
                                >
                                  View All ({org.total})
                                </Typography>
                              )}
                            </Box>
                            <Stack spacing={1}>
                              {org.recent.map((permit) => (
                                <Box
                                  key={permit.id}
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    p: 1.5,
                                    bgcolor: 'rgba(255,255,255,0.02)',
                                    borderRadius: 1,
                                  }}
                                >
                                  <Box>
                                    <Typography variant="body2" sx={{ color: 'white', fontWeight: 500 }}>{permit.permit_number || `#${permit.id}`}</Typography>
                                    <Typography variant="caption" sx={{ color: '#64748b' }}>
                                      <DirectionsCar sx={{ fontSize: 12, mr: 0.5, verticalAlign: 'middle' }} />
                                      {permit.vehicle_number}
                                    </Typography>
                                  </Box>
                                  <Chip
                                    label={permit.status}
                                    size="small"
                                    sx={{
                                      bgcolor: permit.status === 'active' ? 'rgba(16,185,129,0.2)' : permit.status === 'pending' ? 'rgba(245,158,11,0.2)' : 'rgba(239,68,68,0.2)',
                                      color: permit.status === 'active' ? '#10b981' : permit.status === 'pending' ? '#f59e0b' : '#ef4444',
                                    }}
                                  />
                                </Box>
                              ))}
                            </Stack>
                          </>
                        )}
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            )}

            {/* Tab 4: Chalan Revenue */}
            {activeTab === 4 && (
              <Box>
                {/* Revenue Summary Cards */}
                <Grid container spacing={3} sx={{ mb: 4 }}>
                  {[
                    { label: 'Total Revenue', value: stats?.chalan_revenue?.total_revenue || 0, icon: <AttachMoney />, color: '#6366f1' },
                    { label: 'Collected', value: stats?.chalan_revenue?.collected || 0, icon: <Done />, color: '#10b981' },
                    { label: 'Pending', value: stats?.chalan_revenue?.pending || 0, icon: <PendingActions />, color: '#f59e0b' },
                  ].map((item, idx) => (
                    <Grid item xs={12} md={4} key={idx}>
                      <Paper sx={{ p: 3, bgcolor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Box sx={{
                            width: 48,
                            height: 48,
                            borderRadius: 2,
                            bgcolor: `${item.color}20`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}>
                            {React.cloneElement(item.icon, { sx: { color: item.color, fontSize: 24 } })}
                          </Box>
                          <Box>
                            <Typography variant="caption" sx={{ color: '#94a3b8' }}>{item.label}</Typography>
                            <Typography variant="h5" sx={{ color: item.color, fontWeight: 700 }}>
                              {formatCurrency(item.value)}
                            </Typography>
                          </Box>
                        </Box>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>

                <Typography variant="h6" sx={{ color: 'white', fontWeight: 600, mb: 3 }}>
                  Revenue by Organization (Profit Analysis)
                </Typography>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow sx={{ bgcolor: 'rgba(255,255,255,0.04)' }}>
                        {['Organization', 'Total Chalans', 'Total Revenue', 'Collected', 'Pending', 'Collection Rate'].map(h => (
                          <TableCell key={h} sx={{ color: '#94a3b8', fontWeight: 600, borderBottom: '1px solid rgba(255,255,255,0.1)' }}>{h}</TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {stats?.chalan_revenue?.by_org?.map((org) => {
                        const collectionRate = org.total_revenue > 0 ? ((org.collected_revenue / org.total_revenue) * 100).toFixed(1) : 0;
                        return (
                          <React.Fragment key={org.org_id}>
                            <TableRow
                              sx={{ '&:hover': { backgroundColor: 'rgba(255,255,255,0.03) !important' }, cursor: 'pointer' }}
                              onClick={() => toggleOrgExpand(org.org_id)}
                            >
                              <TableCell sx={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                  <IconButton size="small" sx={{ color: '#94a3b8' }}>
                                    {expandedOrg === org.org_id ? <ExpandLess /> : <ExpandMore />}
                                  </IconButton>
                                  <Chip label={org.org_code} size="small" sx={{ bgcolor: 'rgba(99,102,241,0.2)', color: '#818cf8' }} />
                                  <Typography sx={{ color: 'white', fontWeight: 500 }}>{org.org_name}</Typography>
                                </Box>
                              </TableCell>
                              <TableCell sx={{ color: '#94a3b8', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                                {org.total_chalans}
                              </TableCell>
                              <TableCell sx={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                                <Typography sx={{ color: '#6366f1', fontWeight: 600 }}>
                                  {formatCurrency(org.total_revenue)}
                                </Typography>
                              </TableCell>
                              <TableCell sx={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                                <Typography sx={{ color: '#10b981', fontWeight: 600 }}>
                                  {formatCurrency(org.collected_revenue)}
                                </Typography>
                                <Typography variant="caption" sx={{ color: '#64748b' }}>
                                  {org.paid_count} paid
                                </Typography>
                              </TableCell>
                              <TableCell sx={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                                <Typography sx={{ color: '#f59e0b', fontWeight: 600 }}>
                                  {formatCurrency(org.pending_revenue)}
                                </Typography>
                                <Typography variant="caption" sx={{ color: '#64748b' }}>
                                  {org.pending_count} pending
                                </Typography>
                              </TableCell>
                              <TableCell sx={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <LinearProgress
                                    variant="determinate"
                                    value={parseFloat(collectionRate)}
                                    sx={{
                                      width: 60,
                                      height: 6,
                                      borderRadius: 3,
                                      bgcolor: 'rgba(255,255,255,0.1)',
                                      '& .MuiLinearProgress-bar': {
                                        bgcolor: parseFloat(collectionRate) >= 70 ? '#10b981' : parseFloat(collectionRate) >= 40 ? '#f59e0b' : '#ef4444',
                                        borderRadius: 3,
                                      }
                                    }}
                                  />
                                  <Typography sx={{ color: parseFloat(collectionRate) >= 70 ? '#10b981' : parseFloat(collectionRate) >= 40 ? '#f59e0b' : '#ef4444', fontWeight: 600 }}>
                                    {collectionRate}%
                                  </Typography>
                                </Box>
                              </TableCell>
                            </TableRow>
                            <TableRow sx={{ '&:hover': { backgroundColor: 'transparent !important' } }}>
                              <TableCell colSpan={6} sx={{ p: 0, borderBottom: expandedOrg === org.org_id ? '1px solid rgba(255,255,255,0.1)' : 'none' }}>
                                <Collapse in={expandedOrg === org.org_id}>
                                  <Box sx={{ p: 2, bgcolor: 'rgba(255,255,255,0.02)' }}>
                                    <Typography variant="subtitle2" sx={{ color: '#94a3b8', mb: 2 }}>Recent Chalans</Typography>
                                    <Grid container spacing={2}>
                                      {org.recent?.map((chalan) => (
                                        <Grid item xs={12} sm={4} key={chalan.id}>
                                          <Paper sx={{ p: 2, bgcolor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.05)', borderRadius: 1 }}>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                              <Typography variant="body2" sx={{ color: 'white', fontWeight: 500 }}>
                                                {chalan.chalan_number}
                                              </Typography>
                                              <Chip
                                                label={chalan.status}
                                                size="small"
                                                sx={{
                                                  bgcolor: chalan.status === 'paid' ? 'rgba(16,185,129,0.2)' : 'rgba(245,158,11,0.2)',
                                                  color: chalan.status === 'paid' ? '#10b981' : '#f59e0b',
                                                  fontSize: '0.65rem',
                                                }}
                                              />
                                            </Box>
                                            <Typography variant="caption" sx={{ color: '#64748b', display: 'block' }}>
                                              <DirectionsCar sx={{ fontSize: 12, mr: 0.5, verticalAlign: 'middle' }} />
                                              {chalan.vehicle_number}
                                            </Typography>
                                            <Typography variant="body2" sx={{ color: '#10b981', fontWeight: 600, mt: 1 }}>
                                              {formatCurrency(chalan.amount)}
                                            </Typography>
                                          </Paper>
                                        </Grid>
                                      ))}
                                    </Grid>
                                  </Box>
                                </Collapse>
                              </TableCell>
                            </TableRow>
                          </React.Fragment>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}
          </Box>
        </Paper>

        {/* System Health Card */}
        <Paper sx={{
          p: 3,
          bgcolor: '#1e293b',
          border: '1px solid rgba(255,255,255,0.1)',
          borderRadius: 3,
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{
                width: 40,
                height: 40,
                borderRadius: 2,
                bgcolor: 'rgba(16,185,129,0.2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
                <Speed sx={{ color: '#10b981', fontSize: 24 }} />
              </Box>
              <Box>
                <Typography variant="h6" sx={{ color: 'white', fontWeight: 600 }}>
                  System Status
                </Typography>
                <Typography variant="caption" sx={{ color: '#64748b' }}>
                  All systems operational
                </Typography>
              </Box>
            </Box>
            <Chip
              icon={<CheckCircle sx={{ color: '#10b981 !important', fontSize: 16 }} />}
              label="Healthy"
              sx={{ bgcolor: 'rgba(16,185,129,0.15)', color: '#10b981', fontWeight: 600 }}
            />
          </Box>
          <Grid container spacing={3}>
            {[
              { label: 'API Server', status: 100 },
              { label: 'Database', status: 100 },
              { label: 'Storage', status: 85 },
            ].map((service, index) => (
              <Grid item xs={12} sm={4} key={index}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" sx={{ color: '#94a3b8' }}>{service.label}</Typography>
                  <Typography variant="body2" sx={{ color: '#10b981', fontWeight: 600 }}>{service.status}%</Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={service.status}
                  sx={{
                    height: 6,
                    borderRadius: 3,
                    bgcolor: 'rgba(255,255,255,0.1)',
                    '& .MuiLinearProgress-bar': {
                      bgcolor: '#10b981',
                      borderRadius: 3,
                    }
                  }}
                />
              </Grid>
            ))}
          </Grid>
        </Paper>
      </Box>

      {/* View All Permits Dialog (per organization) */}
      <Dialog
        open={Boolean(permitsDialogOrg)}
        onClose={closeOrgPermits}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { bgcolor: '#1e293b', border: '1px solid rgba(255,255,255,0.1)' } }}
      >
        <DialogTitle sx={{ color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box>
            All Permits
            <Typography variant="body2" sx={{ color: '#94a3b8' }}>
              {permitsDialogOrg?.org_name}
            </Typography>
          </Box>
          <IconButton onClick={closeOrgPermits} size="small" sx={{ color: '#94a3b8' }}>
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ borderColor: 'rgba(255,255,255,0.1)' }}>
          {orgPermitsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={32} sx={{ color: '#818cf8' }} />
            </Box>
          ) : orgPermitsError ? (
            <Alert severity="error" sx={{ bgcolor: '#450a0a', color: '#fecaca', border: '1px solid #dc2626' }}>
              {orgPermitsError}
            </Alert>
          ) : orgPermits.length === 0 ? (
            <Typography sx={{ color: '#64748b', textAlign: 'center', py: 4 }}>No permits found</Typography>
          ) : (
            <Stack spacing={1}>
              {orgPermits.map((permit) => (
                <Box
                  key={permit.id}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    p: 1.5,
                    bgcolor: 'rgba(255,255,255,0.02)',
                    borderRadius: 1,
                  }}
                >
                  <Box>
                    <Typography variant="body2" sx={{ color: 'white', fontWeight: 500 }}>{permit.permit_number}</Typography>
                    <Typography variant="caption" sx={{ color: '#64748b' }}>
                      <DirectionsCar sx={{ fontSize: 12, mr: 0.5, verticalAlign: 'middle' }} />
                      {permit.vehicle_number}
                    </Typography>
                  </Box>
                  <Chip
                    label={permit.status}
                    size="small"
                    sx={{
                      bgcolor: permit.status === 'active' ? 'rgba(16,185,129,0.2)' : permit.status === 'pending' ? 'rgba(245,158,11,0.2)' : 'rgba(239,68,68,0.2)',
                      color: permit.status === 'active' ? '#10b981' : permit.status === 'pending' ? '#f59e0b' : '#ef4444',
                    }}
                  />
                </Box>
              ))}
            </Stack>
          )}
        </DialogContent>
        {orgPermitsTotalPages > 1 && !orgPermitsLoading && !orgPermitsError && (
          <DialogActions sx={{ justifyContent: 'center', py: 2 }}>
            <Pagination
              count={orgPermitsTotalPages}
              page={orgPermitsPage}
              onChange={(event, page) => fetchOrgPermits(permitsDialogOrg.org_id, page)}
              size="small"
              sx={{
                '& .MuiPaginationItem-root': { color: '#94a3b8' },
                '& .Mui-selected': { bgcolor: 'rgba(99,102,241,0.3) !important', color: 'white' },
              }}
            />
          </DialogActions>
        )}
      </Dialog>

      {/* View All Users Dialog (per organization) */}
      <Dialog
        open={Boolean(usersDialogOrg)}
        onClose={closeOrgUsers}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { bgcolor: '#1e293b', border: '1px solid rgba(255,255,255,0.1)' } }}
      >
        <DialogTitle sx={{ color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box>
            All Users
            <Typography variant="body2" sx={{ color: '#94a3b8' }}>
              {usersDialogOrg?.org_name}
            </Typography>
          </Box>
          <IconButton onClick={closeOrgUsers} size="small" sx={{ color: '#94a3b8' }}>
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ borderColor: 'rgba(255,255,255,0.1)' }}>
          {orgUsersLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={32} sx={{ color: '#818cf8' }} />
            </Box>
          ) : orgUsersError ? (
            <Alert severity="error" sx={{ bgcolor: '#450a0a', color: '#fecaca', border: '1px solid #dc2626' }}>
              {orgUsersError}
            </Alert>
          ) : orgUsersList.length === 0 ? (
            <Typography sx={{ color: '#64748b', textAlign: 'center', py: 4 }}>No users found</Typography>
          ) : (
            <Stack spacing={1}>
              {orgUsersList.map((user) => (
                <Box
                  key={user.id}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    p: 1.5,
                    bgcolor: 'rgba(255,255,255,0.02)',
                    borderRadius: 1,
                  }}
                >
                  <Avatar sx={{ width: 32, height: 32, bgcolor: user.is_active ? '#10b981' : '#64748b' }}>
                    {user.username?.charAt(0).toUpperCase()}
                  </Avatar>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" sx={{ color: 'white', fontWeight: 500 }}>{user.username}</Typography>
                    <Typography variant="caption" sx={{ color: '#64748b', display: 'block', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {user.email}
                    </Typography>
                  </Box>
                  <Chip
                    label={user.role}
                    size="small"
                    sx={{
                      bgcolor: user.role === 'Admin' ? 'rgba(245,158,11,0.2)' : 'rgba(148,163,184,0.2)',
                      color: user.role === 'Admin' ? '#f59e0b' : '#94a3b8',
                      fontSize: '0.7rem',
                    }}
                  />
                </Box>
              ))}
            </Stack>
          )}
        </DialogContent>
        {orgUsersTotalPages > 1 && !orgUsersLoading && !orgUsersError && (
          <DialogActions sx={{ justifyContent: 'center', py: 2 }}>
            <Pagination
              count={orgUsersTotalPages}
              page={orgUsersPage}
              onChange={(event, page) => fetchOrgUsers(usersDialogOrg.org_id, page)}
              size="small"
              sx={{
                '& .MuiPaginationItem-root': { color: '#94a3b8' },
                '& .Mui-selected': { bgcolor: 'rgba(99,102,241,0.3) !important', color: 'white' },
              }}
            />
          </DialogActions>
        )}
      </Dialog>
    </Box>
  );
};

export default SuperAdminDashboard;